import { PageMetaData } from "@/components";
import { Footer, TopBar, TopNavBar } from "@/layout";
import { getHttpErrorCopy } from "@/constants/httpErrors";
import {
  getSiteErrorVerbosity,
  shouldShowErrorDebugPanel,
  shouldShowErrorStacks,
} from "@/utils/errorVerbosity";
import Link from "next/link";
import {
  BsCloudSlash,
  BsCompass,
  BsExclamationTriangle,
  BsShieldLock,
} from "react-icons/bs";

function ErrorGlyph({ code }) {
  const c = Number(code);
  const common = { "aria-hidden": true, className: "sct-http-error__glyph" };
  if (c === 404) return <BsCompass {...common} />;
  if (c === 401 || c === 403) return <BsShieldLock {...common} />;
  if (c >= 500) return <BsCloudSlash {...common} />;
  return <BsExclamationTriangle {...common} />;
}

/**
 * Shared shell for HTTP-style errors (used by route, 404 catch-all, and error boundary).
 */
export default function HttpErrorContent({
  code,
  titleOverride,
  descriptionOverride,
  debugPayload,
  requestedPath,
  onPrimaryAction,
  primaryActionLabel = "Return to home",
}) {
  const numericCode = Number(code);
  const safeCode =
    Number.isFinite(numericCode) && numericCode >= 100 && numericCode <= 599
      ? Math.floor(numericCode)
      : 500;
  const copy = getHttpErrorCopy(safeCode);
  const title = titleOverride || copy.title;
  const description = descriptionOverride || copy.description;
  const verbosity = getSiteErrorVerbosity();
  const showPanel = shouldShowErrorDebugPanel();
  const showStacks = shouldShowErrorStacks();
  const hasDevPayload =
    debugPayload &&
    (debugPayload.message ||
      debugPayload.detail != null ||
      debugPayload.stack ||
      debugPayload.componentStack);

  const metaTitle =
    safeCode === 404 ? "Page not found" : `${safeCode} ${title}`;

  return (
    <>
      <PageMetaData
        title={metaTitle}
        description={description}
        noIndex
        url={`/errors/${safeCode}`}
      />
      <TopBar />
      <TopNavBar />
      <main className="sct-http-error">
        <div className="sct-http-error__backdrop">
          <div className="container py-5 pb-lg-5">
            <div className="row justify-content-center">
              <div className="col-xl-9 col-lg-10">
                <div className="sct-http-error__card">
                  <div className="sct-http-error__card-inner">
                    <div className="sct-http-error__accent">
                      <span className="sct-http-error__code">{safeCode}</span>
                      <div className="sct-http-error__icon-wrap">
                        <ErrorGlyph code={safeCode} />
                      </div>
                    </div>
                    <div className="sct-http-error__body">
                      <p className="sct-http-error__eyebrow">
                        {safeCode >= 500
                          ? "Server issue"
                          : safeCode === 404
                            ? "Missing page"
                            : "Request issue"}
                      </p>
                      <h1 className="sct-http-error__title">{title}</h1>
                      <p className="sct-http-error__lead">{description}</p>

                      {requestedPath && showPanel && (
                        <div className="sct-http-error__path">
                          <span
                            className="d-block text-uppercase fw-semibold mb-1 small"
                            style={{ letterSpacing: "0.06em" }}
                          >
                            Requested URL
                          </span>
                          <code className="user-select-all">{requestedPath}</code>
                        </div>
                      )}

                      {hasDevPayload && showPanel && (
                        <div className="sct-http-error__dev" role="status">
                          <strong>Technical details</strong>
                          {debugPayload.message && (
                            <div className="mt-2 text-break small text-dark">
                              {String(debugPayload.message)}
                            </div>
                          )}
                          {debugPayload.detail != null && (
                            <pre className="sct-http-error__dev-pre text-muted">
                              {typeof debugPayload.detail === "string"
                                ? debugPayload.detail
                                : JSON.stringify(debugPayload.detail, null, 2)}
                            </pre>
                          )}
                        </div>
                      )}

                      {showStacks &&
                        (debugPayload?.stack || debugPayload?.componentStack) && (
                          <div className="sct-http-error__stack">
                            {debugPayload.stack && (
                              <details>
                                <summary>JavaScript stack trace</summary>
                                <pre>{debugPayload.stack}</pre>
                              </details>
                            )}
                            {debugPayload.componentStack && (
                              <details>
                                <summary>React component stack</summary>
                                <pre>{debugPayload.componentStack}</pre>
                              </details>
                            )}
                          </div>
                        )}

                      <div className="sct-http-error__actions">
                        {typeof onPrimaryAction === "function" ? (
                          <button
                            type="button"
                            className="btn btn-primary sct-http-error__btn-primary"
                            onClick={onPrimaryAction}
                          >
                            {primaryActionLabel}
                          </button>
                        ) : (
                          <Link
                            className="btn btn-primary sct-http-error__btn-primary"
                            href="/"
                          >
                            {primaryActionLabel}
                          </Link>
                        )}
                        <Link
                          className="btn btn-link text-decoration-none px-2"
                          href="/contact"
                        >
                          Contact support
                        </Link>
                      </div>

                      {verbosity === "full" ? (
                        <p className="sct-http-error__hint mb-0">
                          Local development: full diagnostics are visible on this page.
                        </p>
                      ) : verbosity === "verbose" ? (
                        <p className="sct-http-error__hint mb-0">
                          Development build: additional error context is shown above.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
